import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
} from "@/components/ui/dialog";
import { Eye, Info, Trash2, PenLine, InfoIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { blackUser } from "@/assets";
import { ReservationTableItems } from "@/components/dialog/common-dialog";
import * as Tooltip from "@radix-ui/react-tooltip";
import hasPermission from "@/hooks/hasPermission";
import formatTo12HourTime from "@/lib/formatTo12HourTime";

export function ReservationListTable({ data }) {
  const [isNoteOpen, setIsNoteOpen] = React.useState(false);
  const [selectedNote, setSelectedNote] = React.useState("");

  const statusColors = {
    draft: "text-yellow-400",
    waiting: "text-yellow-300/70",
    confirmed: "text-cblue",
    cancelled: "text-cred",
    completed: "text-cgreen",
    on_show: "text-cyellow",
  };

  const [addOnsDialog, setAddOnsDialog] = useState(false);
  const [itemsDialog, setItemsDialog] = useState(null);
  const closeAddOns = () => {
    setAddOnsDialog(false);
    setItemsDialog(null);
  };

  // Helpers: format dates/times in UK locale (Europe/London timezone)
  const formatToUKDate = (isoString) => {
    if (!isoString) return "N/A";
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString("en-GB", { timeZone: "Europe/London" });
    } catch (e) {
      return "N/A";
    }
  };

  const formatToUKDateTime = (isoString) => {
    if (!isoString) return "N/A";
    try {
      const d = new Date(isoString);
      const datePart = d.toLocaleDateString("en-GB", {
        timeZone: "Europe/London",
      });
      const timePart = d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Europe/London",
      });
      return `${datePart} ${timePart}`.replace(/,/g, "");
    } catch (e) {
      return "N/A";
    }
  };


  return (
    <div className="overflow-x-auto w-full">
      <Tooltip.Provider delayDuration={200}>
        <Table className="min-w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Reservation Date</TableHead>
              <TableHead>Issue Date</TableHead>
              <TableHead>Start Time</TableHead>
              <TableHead>End Time</TableHead>
              <TableHead>Guest</TableHead>
              <TableHead>Advance</TableHead>
              <TableHead>Advance Status</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Room/Table</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item?.unique_id || "N/A"}</TableCell>
                  <TableCell>{item?.customer?.full_name || "N/A"}</TableCell>
                  <TableCell>{item?.reservation_type == "phone" ? "Phone Order" : item?.reservation_type == "in_person" ? "In Person" : item?.reservation_type == "online" ? "Online" : "N/A"}</TableCell>
                  <TableCell>
                    {new Date(item?.reservation_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{new Date(item?.issue_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {formatTo12HourTime(item?.start_time)}
                  </TableCell>
                  <TableCell>
                    {formatTo12HourTime(item?.end_time)}
                  </TableCell>
                  <TableCell>{item?.guest || "0"}</TableCell>
                  <TableCell>£{item?.advance || "0"}</TableCell>
                  <TableCell
                    className={
                      item?.is_deposit_paid === 0 ||
                        item?.is_deposit_paid === false
                        ? "text-cred"
                        : "text-cgreen"
                    }
                  >
                    {
                      item?.advance > 0 &&
                      <div className="flex items-center">
                        <span>
                          {item?.is_deposit_paid === 0 ||
                            item?.is_deposit_paid === false
                            ? "Unpaid"
                            : "Paid"}
                        </span>
                        <Tooltip.Root>
                          <Tooltip.Trigger asChild>
                            <InfoIcon
                              size={16}
                              className="text-cwhite3 ml-1 cursor-pointer"
                            />
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content className="bg-black text-white text-xs rounded px-2 py-1">
                              {!item?.is_deposit_paid
                                ? "Deposit not paid"
                                : item?.deposit_payment_info?.method !== "manual"
                                  ? `Paid via ${item?.deposit_payment_info?.provider || "Unknown"}`
                                  : "Paid manually"}
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      </div>
                    }
                  </TableCell>
                  <TableCell
                    className={
                      statusColors[item?.status?.toLowerCase()] ||
                      "text-gray-300"
                    }
                  >
                    <span className="capitalize"> {item?.status || "N/A"}</span>
                  </TableCell>
                  <TableCell>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <Eye
                          onClick={() => {
                            setSelectedNote(item?.note);
                            setIsNoteOpen(true);
                          }}
                          className="text-cwhite3 cursor-pointer"
                          size={18}
                        />
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content className="bg-black text-white text-xs rounded px-2 py-1">
                          View Note
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </TableCell>
                  <TableCell>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <Eye
                          className="text-cwhite3 cursor-pointer"
                          onClick={() => {
                            setAddOnsDialog(true);
                            setItemsDialog(item?.tables);
                          }}
                          size={18}
                        />
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content className="bg-black text-white text-xs rounded px-2 py-1">
                          View Tables
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </TableCell>

                  <TableCell className="text-right justify-end flex gap-2 items-center">
                    <div className="flex items-center gap-2 h-10">
                      {
                        hasPermission("reservation.reservation_list.view_details") && (

                          <Link to={`/reservation/details/${item?.id}`}>
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <Eye
                                  className="text-cwhite3 cursor-pointer"
                                  size={18}
                                />
                              </Tooltip.Trigger>
                              <Tooltip.Portal>
                                <Tooltip.Content className="bg-black text-white text-xs rounded px-2 py-1">
                                  View Reservation Details
                                </Tooltip.Content>
                              </Tooltip.Portal>
                            </Tooltip.Root>
                          </Link>
                        )}
                      {
                        hasPermission("reservation.reservation_list.reservation_update") && (
                          <Link
                            to={"/reservation/reservation-request/update"}
                            state={item}
                            className="cursor-pointer"
                          >
                            <PenLine className="text-cyellow" size={18} />
                          </Link>
                        )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={35} className="text-center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <NoteDialog
          note={selectedNote}
          isOpen={isNoteOpen}
          onClose={() => {
            setIsNoteOpen(false);
            setSelectedNote("");
          }}
        />
        <ReservationTableItems
          items={itemsDialog}
          isOpen={addOnsDialog}
          onClose={closeAddOns}
        />
      </Tooltip.Provider>
    </div>
  );
}

export default ReservationListTable;

const NoteDialog = ({ note, isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reservation Note</DialogTitle>
        </DialogHeader>
        <div className="">
          <p>{note || "No note available"}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
